import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../stores/authStore'
import { oidcEnabled, login } from '../auth/oidc'

/**
 * Auth entry point. Argos has no local login. When Tessera is configured
 * (`VITE_OIDC_AUTHORITY`), this immediately hands off to Tessera's OIDC
 * authorize endpoint via Authorization Code + PKCE (see auth/oidc.ts). In
 * offline dev (no Tessera) it accepts a token directly — e.g. one minted by
 * `POST /v1/dev/token` — so the app is usable against a local backend.
 */
export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const setToken = useAuthStore((s) => s.setToken)
  const [devToken, setDevToken] = useState('')
  const [failed, setFailed] = useState(false)
  const ran = useRef(false)

  useEffect(() => {
    if (!oidcEnabled || ran.current) return
    ran.current = true
    login(params.get('returnTo') ?? '/').catch(() => setFailed(true))
  }, [params])

  if (oidcEnabled) {
    return (
      <p className="p-8 text-neutral-500">
        {failed ? t('login.error') : t('login.tesseraRedirect')}
      </p>
    )
  }

  return (
    <div className="mx-auto mt-24 max-w-sm rounded border border-neutral-200 p-6 dark:border-neutral-800">
      <h1 className="text-lg font-semibold">{t('login.title')}</h1>
      <p className="mt-1 text-sm text-neutral-500">{t('login.devPrompt')}</p>
      <label className="mt-4 block text-sm">
        {t('login.tokenLabel')}
        <textarea
          value={devToken}
          onChange={(e) => setDevToken(e.target.value)}
          className="mt-1 h-24 w-full rounded border border-neutral-300 p-2 font-mono text-xs dark:bg-neutral-800"
        />
      </label>
      <button
        type="button"
        disabled={!devToken.trim()}
        onClick={() => {
          setToken(devToken.trim())
          navigate('/', { replace: true })
        }}
        className="mt-3 rounded bg-neutral-900 px-4 py-2 text-sm text-white disabled:opacity-40 dark:bg-neutral-100 dark:text-neutral-900"
      >
        {t('login.continue')}
      </button>
    </div>
  )
}
